import React, { useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { CircleCheckIcon } from 'icons/common/CircleCheck';
import { BrainIcon } from 'icons/common/Brain';
import { WrenchIcon } from 'icons/common/Wrench';
import { EyeIcon } from 'icons/common/Eye';
import { ZapIcon } from 'icons/common/Zap';
import { SparkleIcon } from 'icons/common/Sparkle';
import { type TraceStep, type TraceStepKind } from './RoutingAIChatTypes';

const TRACE_STEP_META: Record<TraceStepKind, { color: string; bg: string }> = {
  thought: { color: '#7C6BE0', bg: 'rgba(124,107,224,0.10)' },
  tool_call: { color: '#5B8DEF', bg: 'rgba(91,141,239,0.10)' },
  observation: { color: '#0F766E', bg: 'rgba(15,118,110,0.10)' },
  action: { color: '#E0A72A', bg: 'rgba(224,167,42,0.14)' },
};

export const TRACE_KIND_LABELS: Record<TraceStepKind, string> = {
  thought: 'Thought',
  tool_call: 'Tool call',
  observation: 'Observation',
  action: 'Action',
};

export const getTraceStepIcon = (kind: TraceStepKind) => {
  switch (kind) {
    case 'thought':
      return <BrainIcon size={10} color="currentColor" />;
    case 'tool_call':
      return <WrenchIcon size={10} color="currentColor" />;
    case 'observation':
      return <EyeIcon size={10} color="currentColor" />;
    case 'action':
      return <ZapIcon size={10} color="currentColor" />;
    default:
      return <SparkleIcon size={10} color="currentColor" />;
  }
};

type StyledTraceStepIconProps = { traceKind: TraceStepKind };
type StyledTraceKindLabelProps = { traceKind: TraceStepKind };

export const StyledTracePanel = styled.div({
  marginTop: '0.5rem',
  borderRadius: '0.375rem',
  border: '1px solid #e2e8f0',
  backgroundColor: 'rgba(248,250,252,0.7)',
  overflow: 'hidden',
});

export const StyledTraceHeader = styled.button({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0.375rem 0.625rem',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'FiraGO',
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#475569',
  ':hover': { backgroundColor: '#f1f5f9' },
});

export const StyledTraceHeaderLeft = styled.span({
  display: 'flex',
  alignItems: 'center',
  gap: '0.375rem',
});

export const StyledTraceSpin = styled.span({
  display: 'inline-block',
  animation: `${keyframes({ from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } })} 1s linear infinite`,
  lineHeight: 1,
});

export const StyledTraceList = styled.ol({
  padding: '0.25rem 0.625rem 0.5rem',
  margin: 0,
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.375rem',
});

export const StyledTraceStepRow = styled.li({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.5rem',
});

export const StyledTraceStepIcon = styled.span<StyledTraceStepIconProps>(({ traceKind }) => ({
  marginTop: '1px',
  width: '1.125rem',
  height: '1.125rem',
  borderRadius: '0.25rem',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  backgroundColor: TRACE_STEP_META[traceKind].bg,
  color: TRACE_STEP_META[traceKind].color,
}));

export const StyledTraceStepBody = styled.div({
  minWidth: 0,
  flex: 1,
});

export const StyledTraceStepTitle = styled.div({
  display: 'flex',
  alignItems: 'baseline',
  gap: '0.375rem',
  overflow: 'hidden',
});

export const StyledTraceKindLabel = styled.span<StyledTraceKindLabelProps>(({ traceKind }) => ({
  fontSize: '0.625rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: TRACE_STEP_META[traceKind].color,
  fontFamily: 'FiraGO',
  flexShrink: 0,
}));

export const StyledTraceStepLabel = styled.span({
  fontSize: '0.75rem',
  fontWeight: 500,
  color: '#1e293b',
  fontFamily: 'FiraGO',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const StyledTraceStepDetail = styled.div({
  fontSize: '0.6875rem',
  color: '#64748b',
  fontFamily: 'monospace',
  lineHeight: 1.4,
  marginTop: '0.125rem',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
});

export const StyledTraceRunningLabel = styled.span({
  fontWeight: 400,
  letterSpacing: 'normal',
  textTransform: 'none',
  color: '#94a3b8',
});

export const AgentTracePanel = ({
  steps,
  streaming,
  labels,
}: {
  steps: TraceStep[];
  streaming: boolean;
  labels: { agentTrace: string; agentTraceRunning: string };
}) => {
  const [open, setOpen] = useState(true);

  if (!steps.length && !streaming) return null;

  return (
    <StyledTracePanel>
      <StyledTraceHeader type="button" onClick={() => setOpen((o) => !o)}>
        <StyledTraceHeaderLeft>
          {streaming ? (
            <StyledTraceSpin>
              <CircleCheckIcon size={11} color="#94a3b8" />
            </StyledTraceSpin>
          ) : (
            <CircleCheckIcon size={11} color="#059669" />
          )}
          {labels.agentTrace}
          {' · '}
          {steps.length}
          {streaming && (
            <StyledTraceRunningLabel>{labels.agentTraceRunning}</StyledTraceRunningLabel>
          )}
        </StyledTraceHeaderLeft>
        {open ? (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
        )}
      </StyledTraceHeader>
      {open && (
        <StyledTraceList>
          {steps.map((step) => (
            <StyledTraceStepRow key={step.id}>
              <StyledTraceStepIcon traceKind={step.kind}>
                {getTraceStepIcon(step.kind)}
              </StyledTraceStepIcon>
              <StyledTraceStepBody>
                <StyledTraceStepTitle>
                  <StyledTraceKindLabel traceKind={step.kind}>
                    {TRACE_KIND_LABELS[step.kind]}
                  </StyledTraceKindLabel>
                  <StyledTraceStepLabel>{step.label}</StyledTraceStepLabel>
                </StyledTraceStepTitle>
                {step.detail && (
                  <StyledTraceStepDetail>{step.detail}</StyledTraceStepDetail>
                )}
              </StyledTraceStepBody>
            </StyledTraceStepRow>
          ))}
        </StyledTraceList>
      )}
    </StyledTracePanel>
  );
};
